import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import Sidebar from '../admin-dashboard/components/Sidebar';
import AdminBottomNav from '../admin-dashboard/components/AdminBottomNav';
import AdminMobileHeader from '../admin-dashboard/components/AdminMobileHeader';
import CreatorsTable from './components/CreatorsTable';
import CreatorsFilters from './components/CreatorsFilters';
import CreatorsPagination from './components/CreatorsPagination';

const PAGE_SIZE = 50;

const CreatorsManagement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  // Agency filter
  const [agencyFilter, setAgencyFilter] = useState('in_agency');

  // Filter state
  const [search, setSearch] = useState('');
  const [serverFilter, setServerFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [managerFilter, setManagerFilter] = useState('');
  const [managers, setManagers] = useState([]);
  const [whatsappFilter, setWhatsappFilter] = useState('');

  // Table state — always holds the final filtered+paginated slice for display
  const [creators, setCreators] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState('username');
  const [sortDir, setSortDir] = useState('asc');

  const searchDebounceRef = useRef(null);

  // Get current user
  useEffect(() => {
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      if (session?.user?.email) setUserEmail(session?.user?.email);
    });
  }, []);

  // Fetch managers list for filter dropdown
  useEffect(() => {
    const fetchManagers = async () => {
      const { data } = await supabase?.from('users')?.select('id, name')?.in('role', ['manager', 'region_manager'])?.order('name');
      if (data) setManagers(data);
    };
    fetchManagers();
  }, []);

  // Master fetch: always reads from creator_current_month, enriches with agency_status from creators table,
  // then applies agencyFilter client-side to avoid duplicates.
  const fetchCreators = useCallback(async (
    agFilter, page, sort, dir, searchVal, server, status, level, tier, managerId, waFilter
  ) => {
    setLoading(true);
    try {
      // 1. Fetch creator_current_month with server-side filters (only columns that exist in CCM)
      let query = supabase?.from('creator_current_month')?.select('*');

      if (server) query = query?.eq('server', server);
      if (status === 'active_month') query = query?.eq('is_active', true);
      else if (status === 'inactive_month') query = query?.eq('is_active', false);
      if (tier) query = query?.eq('tier_current', parseInt(tier));
      if (managerId) query = query?.eq('manager_id', managerId);

      const { data: currentMonthData, error } = await query;

      if (error) {
        console.error('Creators fetch error:', error);
        setLoading(false);
        return;
      }

      // 2. Fetch creators table for enrichment (whatsapp, display_name, agency_status, level)
      const { data: creatorsData } = await supabase?.from('creators')
        ?.select('tiktok_id, agency_status, departure_date, whatsapp, display_name, level');

      const creatorsMap = {};
      creatorsData?.forEach(r => {
        creatorsMap[r.tiktok_id] = r;
      });

      // 3. Enrich current month data
      const enriched = (currentMonthData || [])?.map(r => ({
        ...r,
        agency_status: creatorsMap?.[r?.tiktok_id]?.agency_status || 'in_agency',
        departure_date: creatorsMap?.[r?.tiktok_id]?.departure_date || null,
        whatsapp: creatorsMap?.[r?.tiktok_id]?.whatsapp || null,
        display_name: creatorsMap?.[r?.tiktok_id]?.display_name || null,
        level: creatorsMap?.[r?.tiktok_id]?.level || null,
      }));

      // 4. Client-side filters: agency, search, whatsapp, level
      let filtered = enriched;

      // Agency filter
      if (agFilter === 'in_agency') {
        filtered = filtered?.filter(r => r?.agency_status !== 'left_agency');
      } else if (agFilter === 'left_agency') {
        filtered = filtered?.filter(r => r?.agency_status === 'left_agency');
      }

      // Search filter (username, tiktok_id, display_name, whatsapp)
      if (searchVal?.trim()) {
        const term = searchVal?.trim()?.toLowerCase();
        filtered = filtered?.filter(r =>
          r?.username?.toLowerCase()?.includes(term) ||
          r?.tiktok_id?.toLowerCase()?.includes(term) ||
          r?.display_name?.toLowerCase()?.includes(term) ||
          r?.whatsapp?.toLowerCase()?.includes(term) ||
          r?.manager_name?.toLowerCase()?.includes(term)
        );
      }

      // Level filter
      if (level) {
        filtered = filtered?.filter(r => r?.level === level);
      }

      // WhatsApp filter
      if (waFilter === 'with') {
        filtered = filtered?.filter(r => r?.whatsapp && r?.whatsapp?.trim() !== '');
      } else if (waFilter === 'without' || waFilter === 'sans') {
        filtered = filtered?.filter(r => !r?.whatsapp || r?.whatsapp?.trim() === '');
      }

      // 5. Client-side sort
      const sortedFiltered = [...filtered]?.sort((a, b) => {
        const aVal = a?.[sort] ?? '';
        const bVal = b?.[sort] ?? '';
        const mult = dir === 'asc' ? 1 : -1;
        if (typeof aVal === 'number' && typeof bVal === 'number') return mult * (aVal - bVal);
        return mult * String(aVal)?.localeCompare(String(bVal));
      });

      // 6. Paginate
      const from = (page - 1) * PAGE_SIZE;
      const paginated = sortedFiltered?.slice(from, from + PAGE_SIZE);

      setTotalCount(sortedFiltered?.length);
      setCreators(paginated);
    } catch (err) {
      console.error('Creators fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced search
  const handleSearch = (val) => {
    setSearch(val);
    setCurrentPage(1);
    if (searchDebounceRef?.current) clearTimeout(searchDebounceRef?.current);
    searchDebounceRef.current = setTimeout(() => {
      fetchCreators(agencyFilter, 1, sortField, sortDir, val, serverFilter, statusFilter, levelFilter, tierFilter, managerFilter, whatsappFilter);
    }, 350);
  };

  const handleServerFilter = (val) => {
    setServerFilter(val);
    setCurrentPage(1);
    fetchCreators(agencyFilter, 1, sortField, sortDir, search, val, statusFilter, levelFilter, tierFilter, managerFilter, whatsappFilter);
  };

  const handleStatusFilter = (val) => {
    setStatusFilter(val);
    setCurrentPage(1);
    fetchCreators(agencyFilter, 1, sortField, sortDir, search, serverFilter, val, levelFilter, tierFilter, managerFilter, whatsappFilter);
  };

  const handleLevelFilter = (val) => {
    setLevelFilter(val);
    setCurrentPage(1);
    fetchCreators(agencyFilter, 1, sortField, sortDir, search, serverFilter, statusFilter, val, tierFilter, managerFilter, whatsappFilter);
  };

  const handleTierFilter = (val) => {
    setTierFilter(val);
    setCurrentPage(1);
    fetchCreators(agencyFilter, 1, sortField, sortDir, search, serverFilter, statusFilter, levelFilter, val, managerFilter, whatsappFilter);
  };

  const handleManagerFilter = (val) => {
    setManagerFilter(val);
    setCurrentPage(1);
    fetchCreators(agencyFilter, 1, sortField, sortDir, search, serverFilter, statusFilter, levelFilter, tierFilter, val, whatsappFilter);
  };

  const handleWhatsappFilter = (val) => {
    setWhatsappFilter(val);
    setCurrentPage(1);
    fetchCreators(agencyFilter, 1, sortField, sortDir, search, serverFilter, statusFilter, levelFilter, tierFilter, managerFilter, val);
  };

  // Sort
  const handleSort = (field) => {
    const newDir = sortField === field && sortDir === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDir(newDir);
    setCurrentPage(1);
    fetchCreators(agencyFilter, 1, field, newDir, search, serverFilter, statusFilter, levelFilter, tierFilter, managerFilter, whatsappFilter);
  };

  // Page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchCreators(agencyFilter, page, sortField, sortDir, search, serverFilter, statusFilter, levelFilter, tierFilter, managerFilter, whatsappFilter);
  };

  // Agency filter change
  const handleAgencyFilter = (val) => {
    setAgencyFilter(val);
    setCurrentPage(1);
    fetchCreators(val, 1, sortField, sortDir, search, serverFilter, statusFilter, levelFilter, tierFilter, managerFilter, whatsappFilter);
  };

  // Initial fetch
  useEffect(() => {
    fetchCreators('in_agency', 1, 'username', 'asc', '', '', '', '', '', '', '');
  }, [fetchCreators]);

  // Read URL params on mount and apply filters
  useEffect(() => {
    const managerParam = searchParams?.get('manager_id');
    const whatsappParam = searchParams?.get('whatsapp');

    if (managerParam || whatsappParam === 'sans') {
      const newManager = managerParam || '';
      const newWhatsapp = whatsappParam === 'sans' ? 'sans' : '';
      if (managerParam) setManagerFilter(managerParam);
      if (whatsappParam === 'sans') setWhatsappFilter('sans');
      fetchCreators('in_agency', 1, 'username', 'asc', '', '', '', '', '', newManager, newWhatsapp);
    }
  }, []);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  // Counter label
  const counterLabel = agencyFilter === 'left_agency' ? t('admin.creators.counterLeft', '— partis') : agencyFilter === 'in_agency' ? t('admin.creators.counterInAgency', '— en agence') : t('admin.creators.counterAll', '— total');

  return (
    <div
      className="flex min-h-screen"
      style={{ backgroundColor: 'var(--color-background)', fontFamily: 'Inter, sans-serif' }}
    >
      <link
        href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Sora:wght@400;500;600;700;800&display=swap"
        rel="stylesheet"
      />
      {/* Sidebar */}
      <div className="hidden md:flex">
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed((v) => !v)}
          userEmail={userEmail}
        />
      </div>
      {/* Main content */}
      <main className="flex-1 overflow-y-auto min-w-0 pb-16 md:pb-0">
        <AdminMobileHeader title={t('admin.creators.title', 'Créateurs')} />
        {/* Header */}
        <div
          className="sticky top-0 z-10 px-6 py-4 border-b"
          style={{ backgroundColor: 'var(--color-background)', borderColor: '#1f1f1f' }}
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1
                className="text-lg font-bold text-white"
                style={{ fontFamily: 'Sora, sans-serif' }}
              >
                {t('admin.creators.title', 'Créateurs')}
              </h1>
              <p
                className="text-xs"
                style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif' }}
              >
                {loading ? (
                  <span className="animate-pulse">{t('admin.creators.loading', 'Chargement...')}</span>
                ) : (
                  <span>
                    <span className="font-semibold" style={{ color: 'rgba(255,255,255,0.6)' }}>{totalCount}</span>
                    {' '}{totalCount !== 1 ? t('admin.creators.creatorsCount', 'créateurs') : t('admin.creators.creatorCount', 'créateur')} {counterLabel}
                  </span>
                )}
              </p>
            </div>
            {/* Export WhatsApp button */}
            <button
              onClick={async () => {
                const { data: ccmData } = await supabase?.from('creator_current_month')?.select('tiktok_id, username, manager_name, server');
                const { data: creatorsWa } = await supabase?.from('creators')?.select('tiktok_id, whatsapp')?.not('whatsapp', 'is', null)?.neq('whatsapp', '');
                const waMap = {};
                creatorsWa?.forEach(c => { waMap[c?.tiktok_id] = c?.whatsapp; });
                const data = ccmData?.filter(r => waMap[r?.tiktok_id])?.map(r => ({ ...r, whatsapp: waMap[r?.tiktok_id] }));
                if (!data || data?.length === 0) return;
                const today = new Date()?.toISOString()?.split('T')?.[0];
                const header = 'tiktok_id,username,whatsapp,manager_name,server';
                const rows = data?.map(r =>
                  `${r?.tiktok_id || ''},${(r?.username || '')?.replace(/,/g, ' ')},${r?.whatsapp || ''},${(r?.manager_name || '')?.replace(/,/g, ' ')},${r?.server || ''}`
                );
                const csv = [header, ...rows]?.join('\n');
                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `whatsapp_export_${today}.csv`;
                a?.click();
                URL.revokeObjectURL(url);
              }}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors hover:opacity-80"
              style={{ backgroundColor: 'rgba(37,211,102,0.15)', color: '#25D366', border: '1px solid rgba(37,211,102,0.3)', fontFamily: 'Inter, sans-serif' }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              {t('admin.creators.exportWhatsapp', 'Exporter WhatsApp')}
            </button>
          </div>

          {/* Agency filter buttons */}
          <div className="flex items-center gap-2 mb-3">
            <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif', marginRight: '4px' }}>{t('admin.creators.agencyLabel', 'Agence:')}</span>
            {[
              { key: 'in_agency', label: t('admin.creators.inAgency', 'En agence') },
              { key: 'left_agency', label: t('admin.creators.leftAgency', 'Partis') },
              { key: 'tous', label: t('admin.creators.all', 'Tous') },
            ]?.map(opt => (
              <button
                key={opt?.key}
                onClick={() => handleAgencyFilter(opt?.key)}
                style={{
                  padding: '4px 12px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  backgroundColor: agencyFilter === opt?.key ? 'rgba(229,32,42,0.15)' : 'transparent',
                  color: agencyFilter === opt?.key ? '#E5202A' : 'rgba(255,255,255,0.4)',
                  border: `1px solid ${agencyFilter === opt?.key ? 'rgba(229,32,42,0.4)' : '#2a2a2a'}`,
                }}
              >
                {opt?.label}
              </button>
            ))}
          </div>

          {/* Filters */}
          <CreatorsFilters
            search={search}
            onSearch={handleSearch}
            serverFilter={serverFilter}
            onServerFilter={handleServerFilter}
            statusFilter={statusFilter}
            onStatusFilter={handleStatusFilter}
            levelFilter={levelFilter}
            onLevelFilter={handleLevelFilter}
            tierFilter={tierFilter}
            onTierFilter={handleTierFilter}
            managerFilter={managerFilter}
            onManagerFilter={handleManagerFilter}
            managers={managers}
            whatsappFilter={whatsappFilter}
            onWhatsappFilter={handleWhatsappFilter}
          />
        </div>

        {/* Content */}
        <div className="p-6">
          <CreatorsTable
            creators={creators}
            loading={loading}
            sortField={sortField}
            sortDir={sortDir}
            onSort={handleSort}
            onCreatorClick={(tiktokId) => navigate(`/admin/creators/${tiktokId}`)}
            agencyFilter={agencyFilter}
          />
          <CreatorsPagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            pageSize={PAGE_SIZE}
            onPageChange={handlePageChange}
          />
        </div>
      </main>
      <AdminBottomNav />
    </div>
  );
};

export default CreatorsManagement;
